import type {ReactNode} from 'react';

import styles from './Roadmap.module.css';

type LexicalUpdateNode = {
  children?: LexicalUpdateNode[];
  format?: number | string;
  listType?: string;
  start?: number;
  text?: string;
  type?: string;
};

const hasTextFormat = (format: LexicalUpdateNode['format'], bit: number, name: string): boolean =>
  typeof format === 'number'
    ? Boolean(format & bit)
    : typeof format === 'string' && format.split(/\s+/).includes(name);

const renderFormattedText = (node: LexicalUpdateNode, key: string): ReactNode => {
  let content: ReactNode = node.text ?? '';

  if (hasTextFormat(node.format, 16, 'code')) {
    content = <code key={`${key}-code`}>{content}</code>;
  }
  if (hasTextFormat(node.format, 1, 'bold')) {
    content = <strong key={`${key}-bold`}>{content}</strong>;
  }
  if (hasTextFormat(node.format, 2, 'italic')) {
    content = <em key={`${key}-italic`}>{content}</em>;
  }
  if (hasTextFormat(node.format, 8, 'underline')) {
    content = <u key={`${key}-underline`}>{content}</u>;
  }
  if (hasTextFormat(node.format, 4, 'strikethrough')) {
    content = <s key={`${key}-strikethrough`}>{content}</s>;
  }

  return content;
};

const renderChildren = (node: LexicalUpdateNode, key: string): ReactNode[] =>
  (node.children ?? []).map((child, index) => renderNode(child, `${key}-${index}`));

const renderList = (node: LexicalUpdateNode, key: string): ReactNode => {
  const isNumbered = node.listType === 'number';
  const start = typeof node.start === 'number' ? node.start : 1;
  let itemIndex = 0;

  return (
    <span className={styles.roadmapUpdateList} key={key}>
      {(node.children ?? []).map((child, index) => {
        if (child.type !== 'listitem') {
          return renderNode(child, `${key}-${index}`);
        }

        const marker = isNumbered ? `${start + itemIndex}.` : '•';
        itemIndex += 1;
        return (
          <span className={styles.roadmapUpdateListItem} key={`${key}-${index}`}>
            <span className={styles.roadmapUpdateListMarker} aria-hidden="true">
              {marker}
            </span>
            <span className={styles.roadmapUpdateListItemContent}>
              {renderChildren(child, `${key}-${index}`)}
            </span>
          </span>
        );
      })}
    </span>
  );
};

const renderNode = (node: LexicalUpdateNode, key: string): ReactNode => {
  switch (node.type) {
    case 'root':
      return <span key={key}>{renderChildren(node, key)}</span>;
    case 'paragraph':
      return (
        <span className={styles.roadmapUpdateParagraph} key={key}>
          {renderChildren(node, key)}
        </span>
      );
    case 'heading':
      return (
        <span className={styles.roadmapUpdateHeading} key={key}>
          {renderChildren(node, key)}
        </span>
      );
    case 'quote':
      return (
        <span className={styles.roadmapUpdateQuote} key={key}>
          {renderChildren(node, key)}
        </span>
      );
    case 'list':
      return renderList(node, key);
    case 'listitem':
      return (
        <span className={styles.roadmapUpdateParagraph} key={key}>
          {renderChildren(node, key)}
        </span>
      );
    case 'linebreak':
      return <br key={key} />;
    case 'text':
      return <span key={key}>{renderFormattedText(node, key)}</span>;
    default:
      if (node.children) {
        return <span key={key}>{renderChildren(node, key)}</span>;
      }
      return node.text ? <span key={key}>{node.text}</span> : null;
  }
};

const parseLexicalUpdate = (value: string): LexicalUpdateNode | null => {
  try {
    const parsed = JSON.parse(value) as {root?: LexicalUpdateNode};
    return parsed.root?.type === 'root' && Array.isArray(parsed.root.children) ? parsed.root : null;
  } catch {
    return null;
  }
};

export const RoadmapUpdateContent = ({value}: {value: string}) => {
  const root = parseLexicalUpdate(value);

  if (!root) {
    return <span className={styles.roadmapUpdateParagraph}>{value}</span>;
  }

  return <>{renderChildren(root, 'roadmap-update')}</>;
};
